// For some reason, Create React App doesn't like the use of discriminate unions in the
// StatementSelectionLogic type, even though it is correct per this article: https://basarat.gitbook.io/typescript/type-system/discriminated-unions
// and does not throw any errors in VS Code.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import React, { ReactNode, SyntheticEvent } from 'react';
import { useHistory } from 'react-router-dom';

import { useStatementSelection } from '../../hooks';
import { FULL_PATHS, URL_PARAMS } from '../../constants';

interface PerformancesTableButtonProps {
    children: ReactNode;
    /**
     * Option callback invoked when button is clicked
     */
    onClick?: (e: SyntheticEvent) => void;
    /**
     * List of search params that will be updated in the URL when the user clicks the button. This
     * is how filter values are passed to the Performances table.
     */
    urlSearchParams: URLSearchParam[];
    /**
     * A {@link StatementSelectionLogic} object which determines which statements will be selected
     * when the user clicks the button.
     */
    statementSelectionLogic: StatementSelectionLogic;
}

/**
 * A button that sends the user to a filtered state of the table view of the Statements page (a.k.a.
 * the Performances table). This includes two actions:
 * - set the URL params for the desired filter states
 * - set the statement selection either from the `presetsWithRegion` or the `statementRange`
 * @param - {@link PerformancesTableButtonProps}
 * @returns JSX
 */
export const PerformancesTableButton = ({
    children,
    onClick,
    urlSearchParams,
    statementSelectionLogic,
}: PerformancesTableButtonProps): JSX.Element => {
    const history = useHistory();
    const {
        statementSelectionProps: { sendStatementSelectionEvent, selectionId },
    } = useStatementSelection();

    const { type } = statementSelectionLogic;

    const statementIds = [];

    if (type === 'statementRange') {
        const { statementIdEnd, statementIdStart } =
            statementSelectionLogic.statementRange;

        if (statementIdEnd) {
            statementIds.push(statementIdEnd);
        }

        if (statementIdStart) {
            statementIds.push(statementIdStart);
        }

        if (statementIds.length > 0) {
            urlSearchParams.push({
                key: URL_PARAMS.STATEMENT_IDS,
                value: statementIds.join('-'),
            });
        }
    }

    return (
        <button
            type="button"
            onClick={(e: SyntheticEvent) => {
                if (onClick) {
                    onClick(e);
                }

                const nextSearch = new URLSearchParams(history.location.search);

                urlSearchParams.forEach(
                    ({ key, value }: URLSearchParam): void => {
                        if (value === null) {
                            nextSearch.delete(key);
                        } else {
                            nextSearch.set(key, encodeURIComponent(value));
                        }
                    },
                );

                // To correctly update URL search params, these function calls
                // need to happen in the following order:

                // First, navigate to Performances table and add the updated search params
                history.push({
                    pathname: FULL_PATHS.STATEMENTS_TABLE,
                    search: `?${nextSearch.toString()}`,
                });

                // Next, send event to select statements
                if (type === 'presetsWithRegion') {
                    sendStatementSelectionEvent({
                        type: 'SET_SELECTED_BY_PRESET_WITH_REGION',
                        value: {
                            id: selectionId,
                            presetsWithRegion:
                                statementSelectionLogic.presetsWithRegion,
                        },
                    });
                } else if (type === 'statementRange') {
                    const {
                        statementRange: { statementIdEnd, statementIdStart },
                    } = statementSelectionLogic;

                    sendStatementSelectionEvent({
                        type: 'SET_SELECTED_BY_STATEMENT_ID_RANGE',
                        value: {
                            id: selectionId,
                            statementIdEnd,
                            statementIdStart,
                        },
                    });
                }
            }}
        >
            {children}
        </button>
    );
};
